import { useLanguage } from "@web/lib/contexts/language-context";
import { useEffect, useRef, useState } from "react";

const CAMERA_TIMEOUT = 10000;

export function useCameraStream() {
	const { t } = useLanguage();

	const [isCameraReady, setIsCameraReady] = useState(false);
	const [cameraError, setCameraError] = useState<string | null>(null);
	const [cameraTimeoutError, setCameraTimeoutError] = useState(false);
	const [facingMode, setFacingMode] = useState<"user" | "environment">(
		"environment",
	);

	const cameraTimeoutRef = useRef<NodeJS.Timeout | null>(null);

	useEffect(() => {
		cameraTimeoutRef.current = setTimeout(() => {
			if (!isCameraReady) {
				setCameraTimeoutError(true);
			}
		}, CAMERA_TIMEOUT);

		return () => {
			if (cameraTimeoutRef.current) clearTimeout(cameraTimeoutRef.current);
		};
	}, [isCameraReady]);

	useEffect(() => {
		(screen.orientation as any)?.lock?.("portrait").catch(() => {});
		return () => {
			screen.orientation?.unlock?.();
		};
	}, []);

	const handleCameraReady = () => {
		setIsCameraReady(true);
		setCameraTimeoutError(false);
		if (cameraTimeoutRef.current) {
			clearTimeout(cameraTimeoutRef.current);
		}
	};

	const handleCameraError = (err: string | DOMException) => {
		console.error("Camera error:", err);
		if (typeof err !== "string" && err.name === "NotAllowedError") {
			setCameraError(t.camera.permissionDenied);
		} else if (typeof err !== "string" && err.name === "NotFoundError") {
			setCameraError(t.camera.cameraNotFound);
		} else {
			setCameraError(t.camera.accessError);
		}
	};

	const retryCamera = () => {
		setIsCameraReady(false);
		setCameraError(null);
		setCameraTimeoutError(false);

		const currentFacingMode = facingMode;
		setFacingMode("user");
		setTimeout(() => {
			setFacingMode(currentFacingMode);
		}, 100);

		if (cameraTimeoutRef.current) clearTimeout(cameraTimeoutRef.current);
		cameraTimeoutRef.current = setTimeout(() => {
			if (!isCameraReady) {
				setCameraTimeoutError(true);
			}
		}, CAMERA_TIMEOUT);
	};

	const resetForRetake = () => {
		setIsCameraReady(false);
	};

	return {
		isCameraReady,
		cameraError,
		cameraTimeoutError,
		facingMode,
		handleCameraReady,
		handleCameraError,
		retryCamera,
		resetForRetake,
	};
}
